import i18n from "@/i18n";
import {
  APOfferVacancyStatus,
  VacancyApplicant,
  VacancyApplicantCreditWorthiness,
  VacancyApplicantEmploymentStatusLevel,
  VacancyApplication,
} from "../../model/VacancyApplicant.interface";
import { BFIndicatorOverlayLevel } from "@/modules/abstract-ui/general/IndicatorOverlay/BFIndicatorOverlay";
import { APActivity } from "@/apps/tatar/activityApp/ActivityInterfaces";
import {
  ActionFlowEntry,
  ActionFlowEntryPublicData,
} from "@/modules/action-flow/model/action-flow.interface";
import { Properties } from "@/utils/Properties";
import { AssetTypes } from "@/model/AssetTypes";
import { ActivityAbstractStructClass } from "@/redux/actions/struct/implemented/ActivityAbstractStruct";
import _ from "lodash";
import { SaluationOptions } from "@/model/db/Contact";

class VacancyOfferUtilsClass {
  getApplicantById = (application: VacancyApplication, applicantId) => {
    return application.data?.applicants?.find((a) => a.id === applicantId);
  };

  isVacancyActionFlowApproval = (
    activity: APActivity,
    activityStruct: ActivityAbstractStructClass<any>
  ) => {
    // check for vacancy config
    const vacancyConfig = activityStruct
      .getAllData()
      .find((s) => s.data.activity_type === AssetTypes.Activity.Vacancy);
    if (!vacancyConfig) {
      return false;
    }

    // check for tags
    const isActionFlowApproval = _.some(activity.data.tags || [], (id) =>
      _.some(vacancyConfig.data.tags, { id, isActionFlowApproval: true })
    );

    return isActionFlowApproval;
  };

  getTokenLink = (entry: ActionFlowEntry) => {
    const url = Properties.actionFlowUrl;
    const entryData: ActionFlowEntryPublicData =
      entry.data as ActionFlowEntryPublicData;
    return `${url}/token/${entryData.token}`;
  };

  getOfferStatusOptions = (): {
    value: APOfferVacancyStatus;
    label: string;
  }[] => {
    return [
      {
        value: "accepted",
        label: i18n.t(
          "apTemplate:Vacancy.Applicant.Offer.Label.Accepted",
          "Mietinteressent genehmigt"
        ),
      },
      {
        value: "contract_sent",
        label: i18n.t(
          "apTemplate:Vacancy.Applicant.Offer.Label.Send",
          "Mietvertrag versendet"
        ),
      },
      {
        value: "signed",
        label: i18n.t(
          "apTemplate:Vacancy.Applicant.Offer.Label.Signed",
          "Mietvertrag vom Mieter unterschrieben"
        ),
      },
      {
        value: "completed",
        label: i18n.t(
          "apTemplate:Vacancy.Applicant.Offer.Label.Ccompleted",
          "Mietvertrag von allen Unterschrieben"
        ),
      },
      {
        value: "send_failed",
        label: i18n.t(
          "apTemplate:Vacancy.Applicant.Offer.Label.SendFailed",
          "Fehler beim Versand des Mietvertrags"
        ),
      },
      {
        value: "revoked",
        label: i18n.t(
          "apTemplate:Vacancy.Applicant.Offer.Label.Revoked",
          "Mietvertrag widerrufen"
        ),
      },
      {
        value: "signed_partially",
        label: i18n.t(
          "apTemplate:Vacancy.Applicant.Offer.Label.Partially",
          "Mietvertrag vom Mieter unterschrieben (andere Unterschriften stehen noch aus)"
        ),
      },
      {
        value: "signed_landlord",
        label: i18n.t(
          "apTemplate:Vacancy.Applicant.Offer.Label.SignedLandlord",
          "Mietvertrag vom Vermieter unterschrieben"
        ),
      },
      {
        value: "contract_created_manual",
        label: i18n.t(
          "apTemplate:Vacancy.Applicant.Offer.Label.contract_created_manual",
          "Mietvertrag als Anhang erstellt"
        ),
      },
      {
        value: "rental_agreement_created",
        label: i18n.t(
          "apTemplate:Vacancy.Applicant.Offer.Label.RentalAgreementCreated",
          "Der Mietvertrag wurde erstellt und im System angelegt"
        ),
      },
    ];
  };

  getOfferStatusLabel = (status: APOfferVacancyStatus) => {
    return (
      this.getOfferStatusOptions().find((s) => s.value === status)?.label ||
      status
    );
  };

  getOfferStatusForContractMQ = (): APOfferVacancyStatus[] => {
    return [
      "accepted",
      "contract_created",
      "contract_sent",
      "completed",
      "send_failed",
      "signed",
      "signed_landlord",
      "signed_partially",
      "revoked",
      "contract_created_manual",
      "contract_building",
      "rental_agreement_created",
    ];
  };

  getOfferStatusForContractLog = (): APOfferVacancyStatus[] => {
    return [
      "contract_created",
      "contract_sent",
      "completed",
      "signed",
      "signed_landlord",
      "signed_partially",
      "rental_agreement_created",
    ];
  };

  getLevelByIncome = (planRent, incomeNet): BFIndicatorOverlayLevel => {
    if (planRent === 0) return "full";
    if (incomeNet === 0) return "warn";

    const incomeNetPercentage = (planRent / incomeNet) * 100;

    return incomeNetPercentage >= 40
      ? "warn"
      : incomeNetPercentage >= 30
      ? "partially"
      : "full";
  };

  getEmploymentStatusLevel = (
    applicant: VacancyApplicant,
    activity: APActivity
  ): VacancyApplicantEmploymentStatusLevel => {
    const employmentStatus = applicant?.employmentInfo?.status;

    if (
      employmentStatus === "employed" ||
      employmentStatus === "civilServant" ||
      employmentStatus === "self-employed"
    ) {
      const level = this.getLevelByIncome(
        activity.data?.meta?.planRent || 0,
        applicant.employmentInfo?.netIncome || 0
      );

      if (level === "warn" || level === "partially") {
        return {
          label: i18n.t(
            "apTemplate:Vacancy.Applicant.EmploymentStatus.Level.Income.Warn",
            "Mietinteressent hat ein zu geringes Einkommen. Eine Bürgschaft wird empfohlen."
          ),
          level: !applicant.guarantor ? level : "full",
          isGuarantorNeeded: true,
          isRentCoverageNeeded: false,
        };
      }
      return {
        level,
        label: i18n.t(
          "apTemplate:Vacancy.Applicant.EmploymentStatus.Level.Income.Full",
          "Mietinteressent hat ein ausreichendes Einkommen"
        ),
      };
    }

    if (employmentStatus === "student") {
      return {
        label: i18n.t(
          "apTemplate:Vacancy.Applicant.EmploymentStatus.Level.student.Message",
          "Mietinteressent ist Student eine Bürgschaft ist notwendig"
        ),
        level: "partially",
        isGuarantorNeeded: true,
        isRentCoverageNeeded: false,
      };
    }

    if (employmentStatus === "lookingForWork") {
      return {
        label: i18n.t(
          "apTemplate:Vacancy.Applicant.EmploymentStatus.Level.lookingForWork.Message",
          "Der Mietinteressent ist auf der Suche nach Arbeit. Eine Bestätigung vom Jobcenter ist notwendig."
        ),
        level: "warn",
        isGuarantorNeeded: false,
        isRentCoverageNeeded: true,
      };
    }

    if (employmentStatus === "other") {
      return {
        label: i18n.t(
          "apTemplate:Vacancy.Applicant.EmploymentStatus.Level.other.Message",
          "Der Mietinteressent hat keine Angaben zu seiner beruflichen Sitation gemacht."
        ),
        level: "warn",
        isGuarantorNeeded: true,
        isRentCoverageNeeded: false,
      };
    }
  };

  getCreditWorthinessLabel = (
    creditWorthiness?: VacancyApplicantCreditWorthiness
  ): string => {
    switch (creditWorthiness) {
      case "full":
        return i18n.t(
          "apTemplate:Vacancy.Applicant.CreditWorthiness.Level.Full",
          "Uneingeschränkte Bonität"
        );
      case "limited":
        return i18n.t(
          "apTemplate:Vacancy.Applicant.CreditWorthiness.Level.limited",
          "Eingeschränkte Bonität"
        );
      case "none":
      case undefined:
      default:
        return i18n.t(
          "apTemplate:Vacancy.Applicant.CreditWorthiness.Level.NotAssigend",
          "Nicht erfasst"
        );
    }
  };

  getCreditWorthinessOptions = () => {
    return [
      {
        value: "none",
        label: i18n.t(
          "apTemplate:Vacancy.Applicant.CreditWorthiness.Level.NotAssigend",
          "Nicht erfasst"
        ),
      },
      {
        value: "limited",
        label: i18n.t(
          "apTemplate:Vacancy.Applicant.CreditWorthiness.Level.limited",
          "Eingeschränkte Bonität"
        ),
      },
      {
        value: "full",
        label: i18n.t(
          "apTemplate:Vacancy.Applicant.CreditWorthiness.Level.Full",
          "Uneingeschränkte Bonität"
        ),
      },
    ];
  };

  getSalutationOption = (applicant: VacancyApplicant): SaluationOptions => {
    return applicant.salutation === "female"
      ? "f"
      : applicant.salutation === "male"
      ? "m"
      : "d";
  };

  getSalutationTextStart = (applicant: VacancyApplicant): string => {
    const salutationStart =
      applicant.salutation === "male"
        ? "Sehr geehrter Herr"
        : applicant.salutation === "female"
        ? "Sehr geehrte Frau"
        : "Guten Tag,";

    return salutationStart;
  };

  getSalutationText = (applicant: VacancyApplicant): string => {
    const salutationStart = this.getSalutationTextStart(applicant);
    return `${salutationStart} ${applicant.firstName} ${applicant.lastName},`;
  };
}

const VacancyOfferUtils = new VacancyOfferUtilsClass();
export default VacancyOfferUtils;
